import React from 'react';
import { Link } from 'react-router-dom';
import ArticlePage from './ArticlePage';

function ExperienceToAlignmentPage() {
  const articleContent = (
    <>
      <h2>From Experience to Alignment: The System Dynamics Edge</h2>
      <p>
        Experiential learning-learning by doing-is the gold standard for modern management. But experience
        alone is a fickle teacher. Two leaders can live through the same market collapse and walk away with
        opposite, and equally incorrect, conclusions.
      </p>
      <p>
        The problem is not the experience; it is the <strong>Mental Model</strong>-the invisible map we use
        to navigate complexity.
      </p>

      <h2>The Invisible Barrier</h2>
      <p>
        In most organizations, decision-making is driven by these implicit maps. Because they are never
        written down, teams often align on <em>goals</em> (where we want to go) while remaining fundamentally
        misaligned on <em>mechanics</em> (how the engine actually works).
      </p>
      <p>
        This gap leads to talking past each other and recurring unintended consequences.
      </p>

      <blockquote
        style={{
          margin: '2rem 0',
          padding: '1.5rem 1.25rem',
          borderLeft: '4px solid #111827',
          backgroundColor: '#f9fafb',
          borderRadius: '0.5rem',
        }}
      >
        <p style={{ margin: 0, fontSize: '1.35rem', lineHeight: 1.4, color: '#111827', fontWeight: 500 }}>
          &ldquo;For the things we have to learn before we can do them, we learn by doing them.&rdquo;
        </p>
        <p style={{ margin: '0.75rem 0 0', fontSize: '0.95rem', color: '#4b5563' }}>
          &mdash; Aristotle, <em>The Nicomachean Ethics</em>
        </p>
      </blockquote>

      <h2>Making Thinking Visible</h2>
      <p>
        System Dynamics (SD) solves this by externalizing the map. Instead of debating opinions, SD asks
        teams to map the <strong>structure</strong> of their reality:
      </p>
      <ul>
        <li><strong>Stocks:</strong> What is accumulating (capital, burnout, technical debt)?</li>
        <li><strong>Flows:</strong> What is the rate of change?</li>
        <li><strong>Feedback Loops:</strong> Where do our actions reinforce or balance the system?</li>
      </ul>

      <h2>From Opinion to Simulation</h2>
      <p>
        The true aha moment happens when these models are simulated. When a team's stated assumptions produce
        a result in a model that contradicts their intuition, it creates <strong>productive tension</strong>.
      </p>
      <p>
        We move from asking <em>Who is right?</em> to <em>What structure produced this behavior?</em>
      </p>

      <h2>The Innovation Payoff</h2>
      <p>
        By shifting from individual intuition to shared, testable models, organizations gain:
      </p>
      <ol>
        <li><strong>Strategic Alignment:</strong> Not just on the what, but the how.</li>
        <li><strong>Evidence-Based Reflection:</strong> Grounding disagreements in structure, not ego.</li>
        <li><strong>Visible Logic:</strong> Closing the gap between experience and actionable insight.</li>
      </ol>
      <p>
        <strong>The Bottom Line:</strong> Real leverage in innovation is not found in more data or more
        experience-it is found in aligning the mental models that interpret them.
      </p>

      <div className="mt-10 p-6 md:p-8 bg-gray-50 border border-gray-200 rounded-lg">
        <p className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">Apply This in Class</p>
        <h3 className="text-2xl font-medium text-gray-900 mb-3">Bring systems thinking to your students</h3>
        <p className="text-gray-600 mb-6">
          Explore how educators are using simulation-driven learning to help students reason through complex,
          real-world decisions.
        </p>
        <Link
          to="/educators"
          className="inline-flex items-center bg-gray-900 text-white px-6 py-3 rounded text-sm font-medium hover:bg-gray-800 transition-colors"
        >
          Explore Veydra for Educators →
        </Link>
      </div>
    </>
  );

  const relatedArticles = [
    { category: 'Article', title: 'System Dynamics in Enterprise Decision Making', href: '/innovation/system-dynamics' },
    { category: 'Article', title: 'Feedback Loops in Business Strategy', href: '/innovation/feedback-loops' },
    { category: 'Article', title: 'From Operational Intelligence to Decision Intelligence', href: '/innovation/decision-intelligence' },
  ];

  return (
    <ArticlePage
      category="Article"
      title="From Experience to Alignment: The System Dynamics Edge"
      subtitle="Why alignment on mental models matters more than shared experience-and how simulation turns assumptions into testable insight."
      heroImage="/images/experiential_learning.png"
      heroImageAspectRatio="12/5"
      articleContent={articleContent}
      relatedArticles={relatedArticles}
    />
  );
}

export default ExperienceToAlignmentPage;
